import {
  ConfluenceLogo,
  JiraServiceDeskLogo,
  JiraSoftwareLogo,
  JiraServiceManagementLogo,
  BitbucketLogo
} from '@atlaskit/logo';
import { B400, B200, N900 } from '@atlaskit/theme/colors';
import EctlRecord, {
  OriginalEctlRecordType
} from '@atlassiansox/bxpkit-ectl-record';
import React, { Fragment, useState } from 'react';
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { messages } from './messages';
import { sortOutRecords } from './utils';

type EctlListPropsType = {
  ectlRecords: [OriginalEctlRecordType];
  contactUsUrl: string;
  activateTrialBaseUrl: string;
  isLoggedIn: boolean;
  loginUrl: string;
  activateUrlParameterKeys?: string[];
  activateOneYearCloudTrial?: boolean;
  isBitbucketEnabled?: boolean;
} & InjectedIntlProps;

function EctlListComponent({
  ectlRecords,
  intl,
  contactUsUrl,
  activateTrialBaseUrl,
  isLoggedIn,
  loginUrl,
  activateUrlParameterKeys,
  activateOneYearCloudTrial,
  isBitbucketEnabled = false
}: EctlListPropsType) {
  const [selectedEctlId, setSelectedEctlId] = useState('');
  const handleSelectedEctlIdChanged = (newValue: string) => {
    setSelectedEctlId(newValue);
  };

  const sortedOutRecords = sortOutRecords(ectlRecords, isBitbucketEnabled);

  if (isLoggedIn) {
    const emptyEligRecords =
      sortedOutRecords.jiraEligRecords.length <= 0 &&
      sortedOutRecords.jsdEligRecords.length <= 0 &&
      sortedOutRecords.jsmEligRecords.length <= 0 &&
      sortedOutRecords.confEligRecords.length <= 0 &&
      sortedOutRecords.bitbucketEligRecords.length <= 0;

    const emptyClaimedRecords =
      sortedOutRecords.jiraClaimedRecords.length <= 0 &&
      sortedOutRecords.jsdClaimedRecords.length <= 0 &&
      sortedOutRecords.jsmClaimedRecords.length <= 0 &&
      sortedOutRecords.confClaimedRecords.length <= 0 &&
      sortedOutRecords.bitbucketClaimedRecords.length < 0;

    const logoProps = {
      iconColor: B200,
      textColor: N900,
      iconGradientStart: B400,
      iconGradientStop: B200,
      size: 'small',
      className: 'atl-product-logo'
    };

    return (
      <EctlList>
        <style>{ectlListCss}</style>
        <div id="eligibleEctls">
          <h2>{intl.formatMessage(messages.eligibleSectionHeader)}</h2>
          {emptyEligRecords ? (
            ''
          ) : (
            <p>{intl.formatMessage(messages.multiEctlInstructions)}</p>
          )}
          <hr />
          {ectlRecords.length > 0 && emptyEligRecords ? (
            <StyledP>
              {intl.formatMessage(messages.noActiveRecordsMessage)}
            </StyledP>
          ) : (
            ''
          )}
          {ectlRecords.length > 0 ? (
            ''
          ) : (
            <Fragment>
              <p>{intl.formatMessage(messages.ineligibleParOne)}</p>
              <p>{intl.formatMessage(messages.ineligibleParTwo)}</p>
              <p>
                {intl.formatMessage(messages.ineligibleParThree)}&nbsp;
                <a href={contactUsUrl}>
                  {intl.formatMessage(
                    messages.ineligibleParFourContactUsLinkText
                  )}
                </a>
              </p>
            </Fragment>
          )}

          {sortedOutRecords.jiraEligRecords.length <= 0 ? (
            ''
          ) : (
            <JiraSoftwareLogo {...logoProps} />
          )}
          {sortedOutRecords.jiraEligRecords.length <= 0
            ? ''
            : sortedOutRecords.jiraEligRecords.map((elem) => (
                <EctlRecord
                  key={elem.entitlementKey}
                  originalEctlRecord={elem}
                  handleSelectedEctlIdChanged={handleSelectedEctlIdChanged}
                  selectedEctlId={selectedEctlId}
                  activateTrialBaseUrl={activateTrialBaseUrl}
                  activateUrlParameterKeys={activateUrlParameterKeys}
                  activateOneYearCloudTrial={activateOneYearCloudTrial}
                />
              ))}

          {sortedOutRecords.jsdEligRecords.length <= 0 ? (
            ''
          ) : (
            <JiraServiceDeskLogo {...logoProps} />
          )}
          {sortedOutRecords.jsdEligRecords.length <= 0
            ? ''
            : sortedOutRecords.jsdEligRecords.map((elem) => (
                <EctlRecord
                  key={elem.entitlementKey}
                  originalEctlRecord={elem}
                  handleSelectedEctlIdChanged={handleSelectedEctlIdChanged}
                  selectedEctlId={selectedEctlId}
                  activateTrialBaseUrl={activateTrialBaseUrl}
                  activateUrlParameterKeys={activateUrlParameterKeys}
                  activateOneYearCloudTrial={activateOneYearCloudTrial}
                />
              ))}

          {sortedOutRecords.jsmEligRecords.length <= 0 ? (
            ''
          ) : (
            <JiraServiceManagementLogo {...logoProps} />
          )}
          {sortedOutRecords.jsmEligRecords.length <= 0
            ? ''
            : sortedOutRecords.jsmEligRecords.map((elem) => (
                <EctlRecord
                  key={elem.entitlementKey}
                  originalEctlRecord={elem}
                  handleSelectedEctlIdChanged={handleSelectedEctlIdChanged}
                  selectedEctlId={selectedEctlId}
                  activateTrialBaseUrl={activateTrialBaseUrl}
                  activateUrlParameterKeys={activateUrlParameterKeys}
                  activateOneYearCloudTrial={activateOneYearCloudTrial}
                />
              ))}

          {sortedOutRecords.confEligRecords.length <= 0 ? (
            ''
          ) : (
            <ConfluenceLogo {...logoProps} />
          )}
          {sortedOutRecords.confEligRecords.length <= 0
            ? ''
            : sortedOutRecords.confEligRecords.map((elem) => (
                <EctlRecord
                  key={elem.entitlementKey}
                  originalEctlRecord={elem}
                  handleSelectedEctlIdChanged={handleSelectedEctlIdChanged}
                  selectedEctlId={selectedEctlId}
                  activateTrialBaseUrl={activateTrialBaseUrl}
                  activateUrlParameterKeys={activateUrlParameterKeys}
                  activateOneYearCloudTrial={activateOneYearCloudTrial}
                />
              ))}

          {sortedOutRecords.bitbucketEligRecords.length <= 0 ? (
            ''
          ) : (
            <BitbucketLogo {...logoProps} />
          )}
          {sortedOutRecords.bitbucketEligRecords.length <= 0
            ? ''
            : sortedOutRecords.bitbucketEligRecords.map((elem) => (
                <EctlRecord
                  key={elem.entitlementKey}
                  originalEctlRecord={elem}
                  handleSelectedEctlIdChanged={handleSelectedEctlIdChanged}
                  selectedEctlId={selectedEctlId}
                  activateTrialBaseUrl={activateTrialBaseUrl}
                  activateUrlParameterKeys={activateUrlParameterKeys}
                  activateOneYearCloudTrial={activateOneYearCloudTrial}
                />
              ))}
        </div>
        <div id="claimedEctls">
          {emptyClaimedRecords ? (
            ''
          ) : (
            <Fragment>
              <h2> {intl.formatMessage(messages.claimedSectionHeader)}</h2>
              <hr />
            </Fragment>
          )}

          {sortedOutRecords.jiraClaimedRecords.length <= 0 ? (
            ''
          ) : (
            <JiraSoftwareLogo {...logoProps} />
          )}
          {sortedOutRecords.jiraClaimedRecords.length <= 0
            ? ''
            : sortedOutRecords.jiraClaimedRecords.map((elem) => (
                <EctlRecord
                  key={elem.entitlementKey}
                  originalEctlRecord={elem}
                  handleSelectedEctlIdChanged={undefined}
                  selectedEctlId={''}
                  activateTrialBaseUrl={activateTrialBaseUrl}
                  activateUrlParameterKeys={activateUrlParameterKeys}
                  activateOneYearCloudTrial={activateOneYearCloudTrial}
                />
              ))}

          {sortedOutRecords.jsdClaimedRecords.length <= 0 ? (
            ''
          ) : (
            <JiraServiceDeskLogo {...logoProps} />
          )}
          {sortedOutRecords.jsdClaimedRecords.length <= 0
            ? ''
            : sortedOutRecords.jsdClaimedRecords.map((elem) => (
                <EctlRecord
                  key={elem.entitlementKey}
                  originalEctlRecord={elem}
                  handleSelectedEctlIdChanged={undefined}
                  selectedEctlId={''}
                  activateTrialBaseUrl={activateTrialBaseUrl}
                  activateUrlParameterKeys={activateUrlParameterKeys}
                  activateOneYearCloudTrial={activateOneYearCloudTrial}
                />
              ))}

          {sortedOutRecords.jsmClaimedRecords.length <= 0 ? (
            ''
          ) : (
            <JiraServiceManagementLogo {...logoProps} />
          )}
          {sortedOutRecords.jsmClaimedRecords.length <= 0
            ? ''
            : sortedOutRecords.jsmClaimedRecords.map((elem) => (
                <EctlRecord
                  key={elem.entitlementKey}
                  originalEctlRecord={elem}
                  handleSelectedEctlIdChanged={undefined}
                  selectedEctlId={''}
                  activateTrialBaseUrl={activateTrialBaseUrl}
                  activateUrlParameterKeys={activateUrlParameterKeys}
                  activateOneYearCloudTrial={activateOneYearCloudTrial}
                />
              ))}

          {sortedOutRecords.confClaimedRecords.length <= 0 ? (
            ''
          ) : (
            <ConfluenceLogo {...logoProps} />
          )}
          {sortedOutRecords.confClaimedRecords.length <= 0
            ? ''
            : sortedOutRecords.confClaimedRecords.map((elem) => (
                <EctlRecord
                  key={elem.entitlementKey}
                  originalEctlRecord={elem}
                  handleSelectedEctlIdChanged={undefined}
                  selectedEctlId={''}
                  activateTrialBaseUrl={activateTrialBaseUrl}
                  activateUrlParameterKeys={activateUrlParameterKeys}
                  activateOneYearCloudTrial={activateOneYearCloudTrial}
                />
              ))}

          {sortedOutRecords.bitbucketClaimedRecords.length <= 0 ? (
            ''
          ) : (
            <BitbucketLogo {...logoProps} />
          )}
          {sortedOutRecords.bitbucketClaimedRecords.length <= 0
            ? ''
            : sortedOutRecords.bitbucketClaimedRecords.map((elem) => (
                <EctlRecord
                  key={elem.entitlementKey}
                  originalEctlRecord={elem}
                  handleSelectedEctlIdChanged={undefined}
                  selectedEctlId={''}
                  activateTrialBaseUrl={activateTrialBaseUrl}
                  activateUrlParameterKeys={activateUrlParameterKeys}
                  activateOneYearCloudTrial={activateOneYearCloudTrial}
                />
              ))}
        </div>
      </EctlList>
    );
  } else {
    return (
      <EctlList>
        <div id="eligibleEctls">
          <h2>{intl.formatMessage(messages.eligibleSectionHeader)}</h2>
          <hr />
          <StyledP>
            {
              <FormattedMessage
                {...messages.loginVerbiage}
                values={{
                  loginCtaHtml: (
                    <a href={loginUrl}>
                      {intl.formatMessage(messages.loginVerbiageLinkText)}
                    </a>
                  )
                }}
              />
            }
          </StyledP>
        </div>
      </EctlList>
    );
  }
}

EctlListComponent.messages = messages;

export default injectIntl(EctlListComponent);

const EctlList = styled.div`
  margin: 10px 10px;
  display: flex;
  flex-direction: column;
  align-items: left;
  justify-content: left;
  position: relative;
  font-family: Charlie Text, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
    Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif;
`;

const ectlListCss = `
.atl-product-logo {
  margin-top: 20px;
}

#claimedEctls {
  margin-top: 42px;
}
`;

const StyledP = styled.p`
  margin-top: 30px;
`;
